import type { ConfigurationContextValue } from "../root/ConfigurationContext";
import type { SponsoringKind } from "../types";
import { getLeadStages, toStageInFilter } from "./stages";

/**
 * The sponsorings resource is shown through three overviews, all rendered by
 * the same (parameterised) list:
 *
 * - "sponsorings": every regular sponsoring (`kind: "sponsoring"`);
 * - "ball": the match-ball sponsors (`kind: "ball"`), which have no contract
 *   type, so that column is dropped from the table and the export;
 * - "leads": every sponsoring of both kinds that is still in the funnel.
 *
 * Each view has its own base path (so the create/edit/show dialogs stay on
 * their own page) and its own store key (so the filters do not leak between
 * the overviews).
 */
export type SponsoringView = "sponsorings" | "ball" | "leads";

type StageConfig = Pick<
  ConfigurationContextValue,
  "sponsoringStages" | "sponsoringActiveStatuses" | "sponsoringClosedStatuses"
>;

export const sponsoringViewBasePath: Record<SponsoringView, string> = {
  sponsorings: "/sponsorings",
  ball: "/balsponsoren",
  leads: "/leads",
};

export const sponsoringViewStoreKey: Record<SponsoringView, string> = {
  sponsorings: "sponsorings.list",
  ball: "sponsorings.ball",
  leads: "sponsorings.leads",
};

/** Page title and navigation label of the view */
export const sponsoringViewTitle: Record<SponsoringView, string> = {
  sponsorings: "resources.sponsorings.views.sponsorings",
  ball: "resources.sponsorings.views.ball",
  leads: "resources.sponsorings.views.leads",
};

/** Label of the create button of the view */
export const sponsoringViewCreateLabel: Record<SponsoringView, string> = {
  sponsorings: "resources.sponsorings.action.new",
  ball: "resources.sponsorings.action.new_ball",
  leads: "resources.sponsorings.action.new_lead",
};

/** Placeholder of the search field (balsponsoren have no contract type) */
export const sponsoringViewSearchPlaceholder: Record<SponsoringView, string> = {
  sponsorings: "resources.sponsorings.filters.search",
  ball: "resources.sponsorings.filters.search_ball",
  leads: "resources.sponsorings.filters.search",
};

/** The kind a record created from this view gets */
export const sponsoringViewKind: Record<SponsoringView, SponsoringKind> = {
  sponsorings: "sponsoring",
  ball: "ball",
  leads: "sponsoring",
};

/** The name of the exported CSV file of the view */
export const sponsoringViewExportName: Record<SponsoringView, string> = {
  sponsorings: "sponsoringen",
  ball: "balsponsoren",
  leads: "leads",
};

/**
 * The permanent filter of the view: the part of the query the user cannot
 * switch off with a quick filter. "sponsorings" and "ball" select their kind,
 * "leads" selects the funnel stages of both kinds.
 */
export const buildViewFilter = (
  view: SponsoringView,
  config: StageConfig,
): Record<string, unknown> =>
  view === "leads"
    ? { "stage@in": toStageInFilter(getLeadStages(config)) }
    : { kind: sponsoringViewKind[view] };
